/**
 * Die Antwortformate der API, wie sie @crm/kontrakte beschreibt.
 *
 * Als eigene Typen und nicht als Import aus dem Vertragspaket: Der Browser
 * bekommt kein zod und keine Serverabhaengigkeit mitgeliefert. Weichen die
 * Formen voneinander ab, faellt es beim ersten Aufruf auf — deshalb steht
 * neben jedem Feld, woher es kommt.
 */

/** Die drei Teile jeder Meldung (ERR-01). */
export type Meldung = {
  readonly wasGeschah: string;
  readonly auswirkung: string;
  readonly naechsterSchritt: string;
};

/** Ein einzelner Verstoss mit Feldpfad (ERR-03, ERR-04). */
export type Verstoss = {
  readonly kennung: string;
  readonly feld?: string;
  readonly meldung: Meldung;
};

export type Fehlerantwort = {
  readonly kennung: string;
  readonly meldung: Meldung;
  /** Fuehrt vom Anwender bis zum Protokolleintrag (ERR-02, LOG-01). */
  readonly vorgang: string;
  readonly verstoesse: readonly Verstoss[];
};

/**
 * Ein Fehler, der aus einer Antwort der API stammt.
 *
 * Er traegt die Meldung mit, statt sie in einen Text zu pressen: Die
 * Oberflaeche zeigt die drei Teile aus ERR-01 getrennt an, und die
 * Verstoesse gehoeren an ihre Felder (ERR-03).
 */
export class ApiFehler extends Error {
  readonly kennung: string;
  readonly meldung: Meldung;
  readonly vorgang: string;
  readonly verstoesse: readonly Verstoss[];
  readonly status: number;

  constructor(antwort: Fehlerantwort, status: number) {
    super(`${antwort.kennung}: ${antwort.meldung.wasGeschah}`);
    this.name = 'ApiFehler';
    this.kennung = antwort.kennung;
    this.meldung = antwort.meldung;
    this.vorgang = antwort.vorgang;
    this.verstoesse = antwort.verstoesse;
    this.status = status;
  }

  /** Der erste Verstoss zu einem Feld — fuer aria-invalid und den Fokus. */
  zumFeld(feld: string): Verstoss | undefined {
    return this.verstoesse.find((v) => v.feld === feld);
  }

  /**
   * ALLE Verstoesse zu einem Feld (ERR-04).
   *
   * "alle Verstoesse werden gemeinsam angezeigt, nicht einer nach dem
   * anderen" — das gilt auch innerhalb eines Feldes. Ein Passwort kann
   * gleichzeitig zu kurz und wiederverwendet sein.
   */
  alleZumFeld(feld: string): readonly Verstoss[] {
    return this.verstoesse.filter((v) => v.feld === feld);
  }

  /**
   * Verstoesse, die keine Maske anzeigt — weil sie kein Feld nennen oder
   * eines, das diese Maske nicht hat.
   *
   * Der zweite Fall ist der gefaehrliche: Ohne ihn verschwaende ein Verstoss
   * zu `neuesPasswort` spurlos, wenn die Maske dieses Feld anders benennt.
   * ERR-04 verlangt, dass ALLE angezeigt werden — also faengt der Kasten auf,
   * was kein Feld aufgefangen hat.
   */
  nichtAmFeld(gezeigteFelder: readonly string[]): readonly Verstoss[] {
    return this.verstoesse.filter(
      (v) => v.feld === undefined || !gezeigteFelder.includes(v.feld),
    );
  }
}

/**
 * Ein Fehler, der die API gar nicht erreicht hat.
 *
 * Er bekommt dieselbe Form wie ein Fehler von dort, damit die Oberflaeche
 * nicht zwei Anzeigewege braucht — und damit auch hier die drei Teile aus
 * ERR-01 dastehen. Eine Meldung "Network Error" waere keine.
 */
export function verbindungsfehler(ursache: unknown): ApiFehler {
  const fehler = new ApiFehler(
    {
      kennung: 'keine_verbindung',
      meldung: {
        wasGeschah: 'Das System war nicht erreichbar.',
        auswirkung:
          'Der Vorgang wurde nicht ausgefuehrt. Ihre Eingaben stehen noch im Formular.',
        naechsterSchritt:
          'Pruefen Sie Ihre Netzverbindung und versuchen Sie es erneut. Bleibt es '
          + 'dabei, wenden Sie sich an die Administration.',
      },
      vorgang: '00000000-0000-4000-8000-000000000000',
      verstoesse: [],
    },
    0,
  );
  // Die eigentliche Ursache haengt als `cause` daran. Sie gehoert in die
  // Entwicklerkonsole und niemals in die Meldung: "Failed to fetch" sagt dem
  // Anwender nichts (ERR-01, ERR-12).
  fehler.cause = ursache;
  return fehler;
}
